import { createFileRoute } from "@tanstack/react-router";
import { ResourceArticle } from "@/components/ResourceArticle";
import { buildBreadcrumbs } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/hero-v2.jpg";

export const Route = createFileRoute("/resources/garage-floor-coating-cost")({
  head: () => ({
    meta: [
      { title: "Garage Floor Coating Cost Guide (2026) | MLC Epoxy Floors Calgary Region" },
      { name: "description", content: "Real 2026 pricing for garage floor coatings in the Calgary region — flake epoxy, metallic, polyaspartic, and grind-and-seal. Per-sq-ft costs and what drives them." },
      { property: "og:title", content: "Garage Floor Coating Cost Guide" },
      { property: "og:description", content: "Real Calgary Region pricing for every coating system." },
      { property: "og:url", content: "/resources/garage-floor-coating-cost" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "/resources/garage-floor-coating-cost" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(buildBreadcrumbs([
        { name: "Home", href: "/" },
        { name: "Resources", href: "/resources" },
        { name: "Cost Guide", href: "/resources/garage-floor-coating-cost" },
      ])),
    }],
  }),
  component: () => (
    <ResourceArticle
      title="Garage Floor Coating Cost Guide"
      intro="What a professional garage floor coating actually costs in Calgary and surrounding areas — broken down by system, prep, and project size."
      image={heroImg}
      sections={[
        {
          type: "prose",
          heading: "How Garage Floor Coatings Are Priced",
          body: [
            "Professional floor coatings in the Calgary region are priced per square foot, not per car bay. The price you're quoted reflects three things: the coating system itself, the concrete prep required, and the condition of your existing slab.",
            "Most homeowners are surprised that prep — diamond grinding, crack repair, and moisture mitigation — often costs more than the resin and flake on top. It's also the single biggest factor in whether a coating lasts 3 years or 20.",
          ],
        },
        {
          type: "table",
          heading: "Average Calgary Region Pricing by System (2026)",
          intro: "Ranges below are turnkey, professionally installed prices including diamond grinding, full-broadcast flake (where applicable), and a polyaspartic top coat. Pricing assumes a typical 400–600 sq ft garage in good structural condition.",
          columns: ["System", "Per sq ft", "2-Car Garage (~450 sq ft)"],
          rows: [
            ["Solid color epoxy + polyaspartic", "$6 – $8", "$2,700 – $3,600"],
            ["Full-broadcast flake + polyaspartic", "$7 – $10", "$3,150 – $4,500"],
            ["Metallic epoxy + polyaspartic", "$10 – $14", "$4,500 – $6,300"],
            ["Grind & seal (decorative)", "$5 – $7", "$2,250 – $3,150"],
            ["Polyaspartic-only one-day system", "$8 – $11", "$3,600 – $4,950"],
          ],
        },
        {
          type: "list",
          heading: "What Drives the Price Up",
          intro: "Two homes on the same street can get quotes that vary by 40%. Here's why:",
          items: [
            { label: "Concrete repairs", desc: "Spalling, pitting, and cracks need to be ground out and filled with polyurea or polyaspartic patch before coating." },
            { label: "Moisture content", desc: "Slabs above 4 lbs/1,000 sq ft/24hr require a moisture-mitigating primer, adding $1–$2 per sq ft." },
            { label: "Old coatings", desc: "Removing failed paint, sealer, or DIY epoxy adds grinding time and disposal cost." },
            { label: "Edge work & coving", desc: "Toe-kick coving up the wall for chemical or commercial environments increases labor." },
            { label: "Color complexity", desc: "Custom flake blends and metallic swirl patterns require extra material and skilled application." },
          ],
        },
        {
          type: "prose",
          heading: "Why DIY Kits Almost Always Cost More",
          body: "Big-box DIY epoxy kits sell for $200–$400 and look attractive on paper. The catch: they skip diamond grinding, use water-based or low-solids resin, and rarely include a UV-stable top coat. Most fail within 2–3 years, leaving homeowners paying for full removal ($2–$4 per sq ft) before a professional system can be installed. The all-in cost of doing it twice is usually higher than getting it done right the first time.",
        },
        {
          type: "prose",
          heading: "Getting an Accurate Quote",
          body: "Any contractor quoting over the phone without seeing your slab is guessing. A real quote requires measuring the space, inspecting for moisture and cracks, and discussing your color and use case (daily driver, workshop, showroom). We provide free on-site estimates throughout the Calgary region — typically within 48 hours of your request.",
        },
      ]}
    />
  ),
});
